/*
 * How each traffic category uses the lines, for every screen that shows it.
 *
 * A category is not one setting. Its own action covers what the box cannot
 * name more closely, and each app under it can carry its own, so a category
 * set to bond can still hold apps that fail over. Showing that category as
 * "Bond" read as a promise its apps then broke, so a category whose apps
 * disagree with it is Mixed, and says which way the exceptions go.
 *
 * The answers come from GET /policy/action (the live action of every class)
 * and GET /classes (which category each app belongs to).
 */

// The categories, in the order the bonding screens list them. BE is the
// catch-all every unmatched flow lands in.
export const CATEGORY_CLASSES = ["BE", "CRITICAL", "REALTIME", "INTERACTIVE", "STREAMING", "BULK", "BACKGROUND", "DIAG"];

/* "spread" and "stripe" are the retired names of what is now one bonding
   action; an edge on an older build can still report them. A class the box
   has not reported yet is treated as failing over, so nothing unloaded ever
   claims to bond. */
export const isBonded = (action) => ["bond", "spread", "stripe"].includes(action);

export const actionOf = (actions, classKey) => (isBonded(actions?.[classKey]) ? "bond" : "failover");

/** appsByCategory groups GET /classes' apps under their category, sorted by
    label: { STREAMING: [{ key, label }], ... }. */
export function appsByCategory(classes, labelOf = (c) => c.label) {
  const byCat = {};
  for (const c of classes || []) {
    if (!c || !c.is_app || !c.category) continue;
    (byCat[c.category] ||= []).push({ key: c.key, label: labelOf(c) });
  }
  for (const cat of Object.keys(byCat)) byCat[cat].sort((a, b) => a.label.localeCompare(b.label));
  return byCat;
}

/**
 * categoryState says how one category uses the lines: "bond", "failover", or
 * "mixed" when an app under it the box reports does otherwise. `differ` lists
 * those apps. An app the box has not reported is left out rather than guessed.
 */
export function categoryState(actions, category, apps) {
  const own = actionOf(actions, category);
  const differ = (apps || []).filter((a) => a.key in (actions || {}) && actionOf(actions, a.key) !== own);
  return { own, state: differ.length ? "mixed" : own, differ };
}

/** mixedWords is the line under a mixed category: "Bond, except 3 apps that fail over". */
export function mixedWords({ own, differ }) {
  const n = differ.length;
  const apps = n === 1 ? "1 app that" : `${n} apps that`;
  return own === "bond" ? `Bond, except ${apps} fail${n === 1 ? "s" : ""} over` : `Failover, except ${apps} bond${n === 1 ? "s" : ""}`;
}

/** Plain names for a state, wherever it is labelled. */
export const STATE_LABEL = { bond: "Bond", failover: "Failover", mixed: "Mixed" };
